import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, TrendUp, FacebookLogo, LinkedinLogo, RedditLogo, Globe } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function BoostModal({ page, boostData, onClose }) {
  const [copied, setCopied] = useState({});

  const copyToClipboard = (text, platform) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [platform]: true });
    toast.success(`Post ${platform} copié !`);
    setTimeout(() => setCopied({ ...copied, [platform]: false }), 2000);
  };

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FacebookLogo,
      color: 'text-blue-500',
      post: boostData.facebook_post,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: LinkedinLogo,
      color: 'text-blue-600',
      post: boostData.linkedin_post,
    },
    {
      id: 'reddit',
      name: 'Reddit',
      icon: RedditLogo,
      color: 'text-orange-500',
      post: boostData.reddit_post,
    },
    {
      id: 'universal',
      name: 'Universel',
      icon: Globe,
      color: 'text-primary',
      post: boostData.universal_message,
    },
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="sticky top-0 glass border-b border-white/10 p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendUp size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Boost Trafic SEO</h2>
                <p className="text-sm text-muted-foreground">Partagez votre page pour obtenir du trafic rapidement</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Estimation trafic */}
            <div className="glass rounded-xl p-6 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Trafic estimé avec ce boost</p>
                  <p className="text-3xl font-bold text-primary">+{boostData.estimated_views} vues</p>
                  <p className="text-xs text-muted-foreground mt-1">Dans les 7 prochains jours</p>
                </div>
                <TrendUp size={48} weight="duotone" className="text-primary opacity-50" />
              </div>
            </div>

            {/* Posts pour chaque plateforme */}
            <div>
              <h3 className="font-semibold mb-4">Posts prêts à partager</h3>
              <div className="space-y-4">
                {platforms.map((platform) => (
                  <div key={platform.id} className="glass rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <platform.icon size={24} weight="fill" className={platform.color} />
                      <span className="font-medium">{platform.name}</span>
                    </div>
                    <div className="bg-background/50 rounded-lg p-4 mb-3">
                      <p className="text-sm whitespace-pre-wrap">{platform.post}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(platform.post, platform.id)}
                      className="w-full"
                      data-testid={`copy-${platform.id}-post`}
                    >
                      {copied[platform.id] ? (
                        <>
                          <Check size={16} className="mr-2" />
                          Copié !
                        </>
                      ) : (
                        <>
                          <Copy size={16} className="mr-2" />
                          Copier le post
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Hashtags */}
            {boostData.hashtags && boostData.hashtags.length > 0 && (
              <div className="glass rounded-xl p-4">
                <h4 className="font-medium mb-3">Hashtags optimisés</h4>
                <div className="flex flex-wrap gap-2">
                  {boostData.hashtags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium cursor-pointer hover:bg-primary/20 transition-colors"
                      onClick={() => {
                        copyToClipboard(boostData.hashtags.join(' '), 'hashtags');
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(boostData.hashtags.join(' '), 'hashtags')}
                  className="mt-2"
                >
                  <Copy size={14} className="mr-2" />
                  Copier tous les hashtags
                </Button>
              </div>
            )}

            {/* Suggestions de communautés */}
            {boostData.suggested_groups && boostData.suggested_groups.length > 0 && (
              <div className="glass rounded-xl p-4">
                <h4 className="font-medium mb-3">Où partager ?</h4>
                <ul className="space-y-2">
                  {boostData.suggested_groups.map((group, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{group}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA final */}
            <div className="glass rounded-xl p-6 text-center bg-gradient-to-r from-primary/5 to-accent/5">
              <p className="font-medium mb-2">💡 Conseil Pro</p>
              <p className="text-sm text-muted-foreground">
                Partagez sur 3-4 plateformes dans les prochaines 24h pour maximiser l'impact.
                Plus de partages = plus de trafic = meilleur SEO !
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
